"use client";

import type { CartOutputDto, CustomerAddressInputDto, ShippingInfoInputDto } from "@sticker-giant/sg-frontend-common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CustomerFacingErrorMessages } from "@/constants/CustomerFacingErrorMessages";
import { SavedInfoSource } from "@/constants/SavedInfoSource";
import { trackAddressSaved } from "@/lib/analytics/segment";
import { CART_ACTIVE_FILTER } from "@/queries/useCart";
import { useGlobalStore } from "@/stores/useGlobalStore";
import { useServicesStore } from "@/stores/useServicesStore";

export const addCustomerAddressMutationKey = ["addCustomerAddress"] as const;

interface AddCustomerAddressInput {
  shippingInfo: ShippingInfoInputDto;
  customerAddressInput: CustomerAddressInputDto;
}

interface AddCustomerAddressResult {
  cart: CartOutputDto;
  accountSaveSucceeded: boolean;
}

export function useAddCustomerAddressMutation() {
  const cartClient = useServicesStore(state => state.cartClient);
  const accountClient = useServicesStore(state => state.accountClientFetch);
  const cartId = useGlobalStore(state => state.cartId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: addCustomerAddressMutationKey,
    mutationFn: async ({
      shippingInfo,
      customerAddressInput,
    }: AddCustomerAddressInput): Promise<AddCustomerAddressResult> => {
      if (!cartClient) throw new Error("Cart client is not available.");
      if (!accountClient) throw new Error("Account client is not available.");
      if (!cartId) throw new Error("Cart id is not available.");

      const accountPromise = accountClient.upsertCustomerAddress(customerAddressInput);
      const cartPromise = cartClient.updateShippingInfo(
        cartId,
        shippingInfo,
        CustomerFacingErrorMessages.unableToAddNewShippingAddressToCart,
      );

      const [accountResult, cartResult] = await Promise.allSettled([accountPromise, cartPromise]);

      if (cartResult.status === "rejected") {
        throw cartResult.reason;
      }

      return { cart: cartResult.value, accountSaveSucceeded: accountResult.status === "fulfilled" };
    },
    onSuccess: ({ cart, accountSaveSucceeded }, { customerAddressInput }) => {
      if (accountSaveSucceeded) trackAddressSaved(SavedInfoSource.CHECKOUT);
      queryClient.setQueriesData<CartOutputDto | null>(CART_ACTIVE_FILTER, cart);
      queryClient.invalidateQueries({ queryKey: ["getCustomerAddresses", customerAddressInput.customerId] });
    },
  });
}
